


import React, { useState, useEffect, useRef } from "react";
import { Card, Spinner, Form, InputGroup, Button, ButtonGroup } from "react-bootstrap";
import StockDetail from "./StockDetail";
import { fetchIndiaQuote } from "../services/marketService";

const API_KEY = process.env.REACT_APP_FINNHUB_KEY;
const US_DEFAULT = ["AAPL", "TSLA", "MSFT", "GOOGL", "AMZN"];
const IN_DEFAULT = ["^NSEI", "^BSESN", "RELIANCE.NS", "TCS.NS", "INFY.NS", "HDFCBANK.NS", "ICICIBANK.NS"];
const IN_LABELS = { "^NSEI": "NIFTY 50", "^BSESN": "SENSEX" };

const fetchUSQuote = async (sym) => {
  const res = await fetch(`https://finnhub.io/api/v1/quote?symbol=${sym}&token=${API_KEY}`);
  return res.json();
};

/* ---------- Shared card ---------- */
const StockRow = ({ label, sub, q, cur, onClick }) => {
  if (!q || !q.c) {
    return (
       <Card className="shadow-sm border-0 py-2 px-3 mb-2">
      <div className="d-flex justify-content-between align-items-center">
        <h6 className="fw-bold mb-0">{label}</h6>
        <small className="text-muted">Data unavailable</small>
      </div>
    </Card>
    );
  }
  const up = q.d >= 0;
  return (
    <Card className="shadow-sm border-0 py-2 px-3 mb-2" style={{ cursor: "pointer" }} onClick={onClick}>
      <div className="d-flex justify-content-between align-items-center">
        <div className="text-start">
          <h6 className="fw-bold mb-0">{label}</h6>
          <small className="text-muted">
            {sub || `H: ${q.h?.toFixed(2)} | L: ${q.l?.toFixed(2)}`}
          </small>
        </div>
        <div className="text-end">
          <h6 className="fw-bold mb-0">{cur}{q.c.toFixed(2)}</h6>
          <small style={{ color: up ? "green" : "red" }}>
            {up ? "+" : ""}{q.d?.toFixed(2)} ({up ? "+" : ""}{q.dp?.toFixed(2)}%)
          </small>
        </div>
      </div>
    </Card>
  );
};

/* ---------- US stocks (Finnhub + WebSocket) ---------- */
const USStocks = () => {
  const [selected, setSelected] = useState(null);
  const [symbols, setSymbols] = useState(US_DEFAULT);
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [input, setInput] = useState("");
  const wsRef = useRef(null);
  const symbolsRef = useRef(symbols);
  symbolsRef.current = symbols;

  useEffect(() => {
    const load = async () => {
      const results = await Promise.all(US_DEFAULT.map(async (s) => [s, await fetchUSQuote(s)]));
      setQuotes(Object.fromEntries(results));
      setLoading(false);
    };
    load().catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const ws = new WebSocket(`wss://ws.finnhub.io?token=${API_KEY}`);
    wsRef.current = ws;
    ws.onopen = () => {
      setLive(true);
      symbolsRef.current.forEach((s) => ws.send(JSON.stringify({ type: "subscribe", symbol: s })));
    };
    ws.onclose = () => setLive(false);
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type !== "trade") return;
      setQuotes((prev) => {
        const next = { ...prev };
        msg.data.forEach((t) => {
          const q = next[t.s] && next[t.s].c ? next[t.s] : { pc: t.p, h: t.p, l: t.p };
          const c = t.p;
          const d = c - q.pc;
          next[t.s] = {
            ...q, c, d,
            dp: q.pc ? (d / q.pc) * 100 : 0,
            h: Math.max(q.h || c, c),
            l: Math.min(q.l || c, c),
          };
        });
        return next;
      });
    };
    return () => ws.close();
  }, []);

  const addSymbol = async () => {
    const s = input.trim().toUpperCase();
    setInput("");
    if (!s || symbols.includes(s)) return;
    setSymbols((prev) => [...prev, s]);
    const q = await fetchUSQuote(s);
    setQuotes((prev) => ({ ...prev, [s]: q }));
    if (wsRef.current?.readyState === WebSocket.OPEN)
      wsRef.current.send(JSON.stringify({ type: "subscribe", symbol: s }));
  };

  return (
    <>
      <div className="text-end mb-2">
        <small style={{ color: live ? "green" : "gray" }}>● {live ? "Live" : "Connecting..."}</small>
      </div>
      <InputGroup className="mb-3">
        <Form.Control
          placeholder="US symbol add karo (e.g. NVDA)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addSymbol()}
        />
        <Button onClick={addSymbol}>Add</Button>
      </InputGroup>

      {selected && (
        <StockDetail market="US" symbol={selected} quote={quotes[selected]} onClose={() => setSelected(null)} />
      )}

      {loading ? (
        <div className="text-center p-2"><Spinner animation="border" /></div>
      ) : (
        symbols.map((sym) => (
          <StockRow key={sym} label={sym} q={quotes[sym]} cur="$" onClick={() => setSelected(sym)} />
        ))
      )}
    </>
  );
};

/* ---------- Indian stocks (backend -> Yahoo Finance) ---------- */
const IndiaStocks = () => {
  const [selected, setSelected] = useState(null);
  const [symbols, setSymbols] = useState(IN_DEFAULT);
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [updated, setUpdated] = useState(null);
  const [input, setInput] = useState("");
  const symbolsRef = useRef(symbols);
  symbolsRef.current = symbols;

  const refresh = async (list) => {
    const results = await Promise.all(
      list.map(async (s) => {
        try { return [s, await fetchIndiaQuote(s)]; }
        catch { return [s, null]; }
      })
    );
    setQuotes((prev) => ({ ...prev, ...Object.fromEntries(results) }));
    setUpdated(new Date());
    setLoading(false);
  };

  useEffect(() => {
    refresh(symbolsRef.current);
    const t = setInterval(() => refresh(symbolsRef.current), 15000);
    return () => clearInterval(t);
  }, []);

  const addSymbol = async () => {
    let s = input.trim().toUpperCase();
    setInput("");
    if (!s) return;
    if (!s.includes(".") && !s.startsWith("^")) s += ".NS";
    if (symbols.includes(s)) return;
    setSymbols((prev) => [...prev, s]);
    refresh([s]);
  };

  return (
    <>
      <div className="text-end mb-2">
        <small className="text-muted">
          {updated ? `Updated ${updated.toLocaleTimeString("en-IN")}` : "Loading..."}
        </small>
      </div>
      <InputGroup className="mb-3">
        <Form.Control
          placeholder="NSE symbol (e.g. SBIN, ITC) ya BSE: 500325.BO"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addSymbol()}
        />
        <Button onClick={addSymbol}>Add</Button>
      </InputGroup>

      {selected && (
        <StockDetail market="IN" symbol={selected} quote={quotes[selected]} onClose={() => setSelected(null)} />
      )}

      {loading ? (
        <div className="text-center p-2"><Spinner animation="border" /></div>
      ) : (
        symbols.map((sym) => (
          <StockRow
            key={sym}
            label={IN_LABELS[sym] || sym.replace(".NS", "").replace(".BO", "")}
            sub={
              quotes[sym]
                ? `${sym.endsWith(".BO") ? "BSE" : sym.startsWith("^") ? "Index" : "NSE"} | H: ${quotes[sym].h?.toFixed(2)} | L: ${quotes[sym].l?.toFixed(2)}`
                : ""
            }
            q={quotes[sym]}
            cur={sym.startsWith("^") ? "" : "₹"}
            onClick={() => setSelected(sym)}
          />
        ))
      )}
    </>
  );
};

/* ---------- Main ---------- */
const MarketData = () => {
  const [market, setMarket] = useState("IN");

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="fw-semibold mb-0">Live Stocks</h5>
        <ButtonGroup size="sm">
          <Button variant={market === "IN" ? "primary" : "outline-primary"} onClick={() => setMarket("IN")}>
            🇮🇳 India
          </Button>
          <Button variant={market === "US" ? "primary" : "outline-primary"} onClick={() => setMarket("US")}>
            🇺🇸 US
          </Button>
        </ButtonGroup>
      </div>

      {market === "IN" ? <IndiaStocks /> : <USStocks />}
    </div>
  );
};

export default MarketData;